import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DateTime } from 'luxon';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import * as XLSX from 'xlsx';

import { PermissionService } from 'app/core/services/permission.service';
import { ApiService } from 'app/shared/apis/api.service';
import { AllocationService, RegulatorClosedReportRow } from 'app/services/allocation.service';
import { AllocationActionsService } from 'app/services/allocation-actions.service';
import { CompanyService } from 'app/services/company.service';
import { ScheduleService } from 'app/services/schedule.service';
import { AutoTranslateService } from 'app/services/auto-translate.service';
import { TimezoneService } from 'app/shared/services/timezone.service';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';
import { RegimeModel, ScheduleEntryModel } from 'app/models/schedule.model';
import {
    ActualAllocationEditorDialogComponent,
    ActualAllocationEditorResult,
    ActualAllocationEditorUser,
} from 'app/modules/shifts/list/actual-allocation-editor-dialog/actual-allocation-editor-dialog.component';
import {
    StartAllocationScopeDialogComponent,
    StartAllocationScopeResult,
} from 'app/modules/shifts/list/start-allocation-scope-dialog/start-allocation-scope-dialog.component';
import {
    RegulatorPlannedFormDialogComponent,
    RegulatorPlannedFormResult,
} from './regulator-planned-form-dialog/regulator-planned-form-dialog.component';
import {
    attachRegulatorPlannedBlocks,
    buildRegulatorTimeline,
    RegulatorTimelineBlock,
    RegulatorTimelineModel,
} from './regulator-widget.utils';

const HOUR_COLUMNS = Array.from({ length: 24 }, (_, h) => h);

@Component({
    selector: 'app-regulator-widget',
    standalone: true,
    imports: [CommonModule, MatIconModule, MatButtonModule, MatMenuModule, MatTooltipModule, AutoTranslatePipe],
    templateUrl: './regulator-widget.component.html',
    styleUrls: ['./regulator-widget.component.scss'],
})
export class RegulatorWidgetComponent implements OnInit, OnDestroy {
    private readonly _unsubscribeAll = new Subject<void>();

    expanded = false;
    loading = false;
    exporting = false;
    visible = false;
    canEdit = false;
    timeline: RegulatorTimelineModel = { sections: [], nowLeftPct: null };
    readonly hourColumns = HOUR_COLUMNS;
    selectedDate = DateTime.now();
    dateLabel = '';
    isToday = true;
    showPlanned = false;

    constructor(
        private readonly companyService: CompanyService,
        private readonly allocationService: AllocationService,
        private readonly allocationActions: AllocationActionsService,
        private readonly scheduleService: ScheduleService,
        private readonly permissionService: PermissionService,
        private readonly autoTranslate: AutoTranslateService,
        private readonly dialog: MatDialog,
        readonly timezoneService: TimezoneService,
    ) {}

    ngOnInit(): void {
        const tz = this.timezoneService.getTimezone();
        this.selectedDate = DateTime.now().setZone(tz);
        this.refreshDateLabel();
        this.canEdit = this.canManageRegulatorAllocations();
        this.allocationActions.loadAccessibleCompanies();

        this.companyService.currentCompany$.pipe(takeUntil(this._unsubscribeAll)).subscribe((company) => {
            this.visible = company?.company_id != null && this.canViewRegulatorWidget();
            if (this.visible) this.load();
        });
    }

    /** Médico regulador precisa ver a própria equipe remota sem SHIFT_VIEW / cockpit. */
    private canViewRegulatorWidget(): boolean {
        if (this.permissionService.hasPermission('SHIFT_VIEW')) return true;
        if (this.permissionService.hasPermission('COMPANY_COCKPIT_VIEW')) return true;
        if (this.permissionService.hasPermission('SERVICE_VIEW')) return true;
        return !!ApiService.user.value?.is_regulator;
    }

    private canManageRegulatorAllocations(): boolean {
        if (this.permissionService.hasPermission('SHIFT_UPDATE')) return true;
        if (this.permissionService.hasPermission('COMPANY_COCKPIT_VIEW')) return true;
        if (!ApiService.user.value?.is_regulator) return false;
        return (
            this.permissionService.hasPermission('SERVICE_CREATE') ||
            this.permissionService.hasPermission('SERVICE_UPDATE')
        );
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    /** Nº de reguladores com alocação aberta (online agora), únicos entre todas as empresas. */
    get onlineCount(): number {
        const userIds = new Set<number>();
        for (const section of this.timeline.sections) {
            for (const block of section.blocks) {
                if (block.isOpen) userIds.add(block.userId);
            }
        }
        return userIds.size;
    }

    toggle(): void {
        this.expanded = !this.expanded;
        if (this.expanded) this.load();
    }

    togglePlanned(): void {
        this.showPlanned = !this.showPlanned;
        if (this.showPlanned) this.load();
    }

    previousDay(): void {
        this.selectedDate = this.selectedDate.minus({ days: 1 });
        this.refreshDateLabel();
        this.load();
    }

    nextDay(): void {
        this.selectedDate = this.selectedDate.plus({ days: 1 });
        this.refreshDateLabel();
        this.load();
    }

    goToToday(): void {
        this.selectedDate = DateTime.now().setZone(this.timezoneService.getTimezone());
        this.refreshDateLabel();
        this.load();
    }

    private refreshDateLabel(): void {
        const tz = this.timezoneService.getTimezone();
        const today = DateTime.now().setZone(tz);
        this.isToday = this.selectedDate.hasSame(today, 'day');
        this.dateLabel = this.selectedDate.setLocale('pt-BR').toFormat('dd/MM');
    }

    /** Exporta Excel dos plantões finalizados dos reguladores no mês do dia selecionado — cross-company. */
    async exportReport(): Promise<void> {
        if (this.exporting) return;
        this.exporting = true;
        const tz = this.timezoneService.getTimezone();
        const monthKey = this.selectedDate.toFormat('yyyy-MM');
        try {
            const rows = await this.allocationService.getRegulatorsClosedReport(monthKey, tz).promise;
            if (!rows?.length) return;
            await this.downloadClosedAllocationsExcel(rows, monthKey);
        } catch (err) {
            console.error('[RegulatorWidget] falha ao exportar relatório', err);
        } finally {
            this.exporting = false;
        }
    }

    /** Clique num plantão existente: abre o mesmo editor de /shifts/allocations (fechar/editar/remover), sem viatura. */
    onBlockClick(block: RegulatorTimelineBlock): void {
        if (!this.canEdit) return;
        const tz = this.timezoneService.getTimezone();
        const dateKey = this.selectedDate.toFormat('yyyy-MM-dd');
        const ref = this.dialog.open(ActualAllocationEditorDialogComponent, {
            width: 'min(90vw, 28rem)',
            data: {
                dateKey,
                timezone: tz,
                role: 'DOCTOR',
                roleLabel: 'Médico',
                shiftId: null,
                vehicleId: null,
                users: [],
                crew: [{ user_id: block.userId, name: block.userName }],
                clickedUserId: block.userId,
                existing: {
                    allocation_id: block.allocationId,
                    isOpen: block.isOpen,
                    label: block.userName,
                    started_at: block.startedAt,
                    ended_at: block.endedAt,
                },
            },
        });
        ref.afterClosed().subscribe((result: ActualAllocationEditorResult | undefined) => {
            if (!result || result.mode === 'cancel' || result.mode === 'add-person') return;
            const companyId = block.companyId;
            if (result.mode === 'close') {
                this.allocationActions.promptMultiCompanyScope(companyId, 'close').subscribe((companyIds) => {
                    if (!companyIds) return;
                    if (result.scope === 'person' && result.user_id) {
                        this.allocationActions.removePersonAcrossCompanies(
                            companyId,
                            result.allocation_id,
                            result.user_id,
                            companyIds,
                            () => this.load(),
                        );
                        return;
                    }
                    this.allocationActions.closeAcrossCompanies(
                        companyId,
                        result.allocation_id,
                        result.ended_at,
                        [block.userId],
                        companyIds,
                        () => this.load(),
                    );
                });
                return;
            }
            if (result.mode === 'delete') {
                this.allocationActions.promptMultiCompanyScope(companyId, 'delete').subscribe((companyIds) => {
                    if (!companyIds) return;
                    this.allocationActions.deleteAcrossCompanies(companyId, result.allocation_id, [block.userId], companyIds, () =>
                        this.load(),
                    );
                });
                return;
            }
            if (result.mode === 'update') {
                this.allocationActions.promptMultiCompanyScope(companyId, 'update').subscribe((companyIds) => {
                    if (!companyIds) return;
                    this.allocationActions.updateScheduleAcrossCompanies(
                        companyId,
                        result.allocation_id,
                        result.started_at,
                        result.ended_at ?? undefined,
                        [block.userId],
                        companyIds,
                        () => this.load(),
                    );
                });
            }
        });
    }

    /** Botão "Novo +": escolhe reguladores e em quais empresas iniciar o plantão — sempre sem viatura. */
    async startNewAllocation(): Promise<void> {
        if (!this.canEdit) return;
        let regulators: { user_id: number; name: string }[] = [];
        try {
            regulators = (await this.allocationService.getRegulatorsList().promise) || [];
        } catch {
            regulators = [];
        }
        if (!regulators.length) return;

        const scopeUsers = regulators.map((r) => ({ user_id: r.user_id, name: r.name, profile_type: 'DOCTOR' }));
        const scopeRef = this.dialog.open(StartAllocationScopeDialogComponent, {
            width: 'min(94vw, 28rem)',
            data: { role: 'DOCTOR', roleLabel: 'Médico', users: scopeUsers, forceCrewMode: true },
        });
        scopeRef.afterClosed().subscribe((scope: StartAllocationScopeResult | undefined) => {
            if (!scope || scope.mode !== 'crew') return;
            this.openNewAllocationForm(scope.users);
        });
    }

    private openNewAllocationForm(presetUsers: ActualAllocationEditorUser[]): void {
        const dateKey = this.selectedDate.toFormat('yyyy-MM-dd');
        const ref = this.dialog.open(ActualAllocationEditorDialogComponent, {
            width: 'min(90vw, 28rem)',
            data: {
                dateKey,
                timezone: this.timezoneService.getTimezone(),
                role: 'DOCTOR',
                roleLabel: 'Médico',
                shiftId: null,
                vehicleId: null,
                users: [],
                presetUsers,
            },
        });
        ref.afterClosed().subscribe((result: ActualAllocationEditorResult | undefined) => {
            if (!result || result.mode !== 'save') return;
            const userNameById = new Map(presetUsers.map((u) => [u.user_id, u.name]));
            this.allocationActions.checkOpenAllocationThenContinue(
                this.timezoneService.getTimezone(),
                result.users,
                null,
                userNameById,
                () => this.saveNewAllocation(result),
            );
        });
    }

    private saveNewAllocation(result: Extract<ActualAllocationEditorResult, { mode: 'save' }>): void {
        this.allocationActions.promptCompanyScopeNoDefault('create').subscribe((companyIds) => {
            if (!companyIds?.length) return;
            this.allocationActions.createDirectAcrossCompanies(
                { vehicleId: null, shiftId: null, startedAt: result.started_at, users: result.users },
                companyIds,
                () => this.load(),
            );
        });
    }

    private async downloadClosedAllocationsExcel(rows: RegulatorClosedReportRow[], monthKey: string): Promise<void> {
        const [nome, empresa, dataInicio, dataFim, qtdeHoras, sheetName] = await Promise.all([
            this.autoTranslate.translateSync('Nome'),
            this.autoTranslate.translateSync('Empresa'),
            this.autoTranslate.translateSync('Data de Início'),
            this.autoTranslate.translateSync('Data de Fim'),
            this.autoTranslate.translateSync('Qtde Horas'),
            this.autoTranslate.translateSync('Relatório'),
        ]);

        const tz = this.timezoneService.getTimezone();
        const body: (string | number)[][] = rows.map((row) => [
            row.name,
            row.company,
            DateTime.fromJSDate(new Date(row.started_at), { zone: tz }).toFormat('dd/MM/yyyy HH:mm'),
            DateTime.fromJSDate(new Date(row.ended_at), { zone: tz }).toFormat('dd/MM/yyyy HH:mm'),
            row.hours,
        ]);

        const sheet = XLSX.utils.aoa_to_sheet([[nome, empresa, dataInicio, dataFim, qtdeHoras], ...body]);
        sheet['!cols'] = [{ wch: 28 }, { wch: 24 }, { wch: 18 }, { wch: 18 }, { wch: 12 }];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, sheet, sheetName.slice(0, 31));
        XLSX.writeFile(workbook, `relatorio-equipe-remota-${monthKey}.xlsx`);
    }

    private async load(): Promise<void> {
        this.loading = true;
        const tz = this.timezoneService.getTimezone();
        const dateKey = this.selectedDate.toFormat('yyyy-MM-dd');

        try {
            const allocations = await this.allocationService.getByRegulatorsDay(dateKey, tz).promise;
            const timeline = buildRegulatorTimeline(allocations || [], dateKey, tz);

            if (this.showPlanned) {
                const [entries, regimes] = await Promise.all([
                    firstValueFrom(this.scheduleService.getRegulatorsDay(dateKey)).catch(() => [] as ScheduleEntryModel[]),
                    this.loadAccessibleRegimes(),
                ]);
                const companiesById = new Map(this.allocationActions.accessibleCompanies.map((c) => [c.company_id, c.name]));
                attachRegulatorPlannedBlocks(timeline, entries || [], regimes, companiesById);
            }

            this.timeline = timeline;
        } catch (err) {
            console.error('[RegulatorWidget] falha ao carregar escala remota', err);
            this.timeline = { sections: [], nowLeftPct: null };
        } finally {
            this.loading = false;
        }
    }

    /** Regimes de todas as empresas acessíveis — necessário pra resolver o horário do previsto cross-company. */
    private async loadAccessibleRegimes(): Promise<RegimeModel[]> {
        const companies = this.allocationActions.accessibleCompanies;
        if (!companies.length) return [];
        const perCompany = await Promise.all(
            companies.map((c) =>
                firstValueFrom(this.scheduleService.listRegimes(c.company_id)).catch(() => [] as RegimeModel[]),
            ),
        );
        return perCompany.flat();
    }

    /** Botão "Previsto +": escolhe regulador, empresa e intervalo de datas pra criar/atualizar a escala prevista. */
    async addPlanned(): Promise<void> {
        if (!this.canEdit) return;
        let regulators: { user_id: number; name: string }[] = [];
        try {
            regulators = (await this.allocationService.getRegulatorsList().promise) || [];
        } catch {
            regulators = [];
        }
        if (!regulators.length || !this.allocationActions.accessibleCompanies.length) return;

        const ref = this.dialog.open(RegulatorPlannedFormDialogComponent, {
            width: 'min(94vw, 26rem)',
            data: {
                dateKey: this.selectedDate.toFormat('yyyy-MM-dd'),
                regulators,
                companies: this.allocationActions.accessibleCompanies,
            },
        });
        ref.afterClosed().subscribe(async (result: RegulatorPlannedFormResult | undefined) => {
            if (!result || result.mode !== 'save') return;
            try {
                await Promise.all(
                    result.user_ids.flatMap((userId) =>
                        result.company_ids.map((companyId) =>
                            firstValueFrom(
                                this.scheduleService.upsertRegulatorEntries({
                                    user_id: userId,
                                    company_id: companyId,
                                    date_start: result.date_start,
                                    date_end: result.date_end,
                                    start_time: result.start_time,
                                    end_time: result.end_time,
                                }),
                            ),
                        ),
                    ),
                );
                this.load();
            } catch (err) {
                console.error('[RegulatorWidget] falha ao salvar previsto', err);
            }
        });
    }
}
